// Cart Utilities - AJAX Cart Management with Pub/Sub System
class CartManager {
  constructor() {
    this.subscribers = [];
    this.cartData = null;
    this.init();
  }

  init() {
    // Fetch initial cart data
    this.fetchCartData();
    
    // Listen for external cart update events (not from our own notify calls)
    document.addEventListener('cart:external-update', () => {
      this.fetchCartData();
    });
  }

  // Subscribe to cart updates
  subscribe(callback) {
    this.subscribers.push(callback);
  }

  // Unsubscribe from cart updates
  unsubscribe(callback) {
    this.subscribers = this.subscribers.filter(sub => sub !== callback);
  }

  // Notify all subscribers of cart changes
  notify(cartData) {
    this.subscribers.forEach(callback => callback(cartData));
    
    // Dispatch event for external components (not our own cart manager)
    document.dispatchEvent(new CustomEvent('cart:data-updated', {
      detail: { cartData }
    }));
  }

  // Fetch current cart data
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        this.cartData = await response.json();
        this.notify(this.cartData);
        return this.cartData;
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
    return null;
  }

  // Reject items whose unit price is $0. Real "free" lines (warranty add-on,
  // gift wrap, etc.) are attached server-side via cart_transform, not by the
  // customer adding them directly — so it's safe to refuse zero-price adds here.
  // The Shopify Function `zero-price-validation` is the authoritative guard;
  // this client check exists so legitimate browsers see the error immediately
  // instead of being told at checkout.
  async _rejectIfZeroPriceLine(addedItem) {
    if (!addedItem) return false;
    const price = addedItem.final_price ?? addedItem.price;
    if (typeof price === 'number' && price === 0) {
      try {
        const id = addedItem.variant_id || addedItem.id;
        if (id) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, quantity: 0 }),
          });
        }
      } catch (_) {}
      await this.fetchCartData();
      return true;
    }
    return false;
  }

  // Add item to cart
  async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      });

      if (response.ok) {
        const addedItem = await response.json();
        if (await this._rejectIfZeroPriceLine(addedItem)) {
          return { success: false, error: 'This item is not available for direct purchase. Please contact us for pricing.' };
        }
        await this.fetchCartData(); // Refresh cart data

        // Dispatch item added event
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: addedItem, variantId, quantity }
        }));

        return { success: true, item: addedItem };
      } else {
        const error = await response.json();
        return { success: false, error: error.description || 'Failed to add item to cart' };
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Add item to cart via multipart/form-data (required when uploading files).
  // formData should be a FormData containing at least `id` and `quantity`,
  // plus any properties[Key]=value or file inputs.
  async addToCartFormData(formData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        // No Content-Type header — browser sets multipart boundary automatically.
        body: formData,
      });

      if (response.ok) {
        const addedItem = await response.json();
        if (await this._rejectIfZeroPriceLine(addedItem)) {
          return { success: false, error: 'This item is not available for direct purchase. Please contact us for pricing.' };
        }
        await this.fetchCartData();

        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: addedItem }
        }));

        return { success: true, item: addedItem };
      }

      const error = await response.json().catch(() => ({}));
      return { success: false, error: error.description || 'Failed to add item to cart' };
    } catch (error) {
      console.error('Error adding to cart (multipart):', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Add multiple items to cart in a single atomic request.
  // items: [{ id, quantity, properties }]
  // If any item fails, none are added.
  async addMultipleToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        const result = await response.json();
        const added = result.items || [];
        const zeroLine = added.find((it) => (it.final_price ?? it.price) === 0);
        if (zeroLine) {
          // Roll back the whole atomic add — easier than per-line removal.
          for (const it of added) {
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: it.variant_id || it.id, quantity: 0 }),
              });
            } catch (_) {}
          }
          await this.fetchCartData();
          return { success: false, error: 'One or more items are not available for direct purchase. Please contact us for pricing.' };
        }
        await this.fetchCartData();

        document.dispatchEvent(new CustomEvent('cart:items-added', {
          detail: { items: added, requested: items }
        }));

        return { success: true, items: added };
      } else {
        const error = await response.json();
        return { success: false, error: error.description || 'Failed to add items to cart' };
      }
    } catch (error) {
      console.error('Error adding multiple items to cart:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Update cart item quantity
  async updateCartItem(variantId, quantity) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity
          }
        })
      });

      if (response.ok) {
        await this.fetchCartData(); // Refresh cart data
        return { success: true };
      } else {
        return { success: false, error: 'Failed to update cart' };
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Update multiple cart items in a single atomic request.
  // updates: { variantId1: quantity1, variantId2: quantity2, ... }
  // Setting a quantity to 0 removes that item.
  async updateMultipleCartItems(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        await this.fetchCartData();
        return { success: true };
      } else {
        return { success: false, error: 'Failed to update cart' };
      }
    } catch (error) {
      console.error('Error updating multiple cart items:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Remove item from cart
  async removeFromCart(variantId) {
    return this.updateCartItem(variantId, 0);
  }

  // Update a specific line item by its unique line key.
  // Use this from per-line UI (cart page, drawer) — variant-id based updates
  // collide when the cart has multiple lines for the same variant (e.g. two
  // shirts with different `properties[Custom Name]`).
  async updateCartItemByKey(lineKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (response.ok) {
        await this.fetchCartData();
        return { success: true };
      }
      return { success: false, error: 'Failed to update cart' };
    } catch (error) {
      console.error('Error updating cart line:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  async removeFromCartByKey(lineKey) {
    return this.updateCartItemByKey(lineKey, 0);
  }

  // Clear entire cart
  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        await this.fetchCartData(); // Refresh cart data
        return { success: true };
      } else {
        return { success: false, error: 'Failed to clear cart' };
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }

  // Get current cart count
  getCartCount() {
    return this.cartData ? this.cartData.item_count : 0;
  }

  // Get current cart total
  getCartTotal() {
    return this.cartData ? this.cartData.total_price : 0;
  }

  // Format price helper
  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }

  // Format image URL helper
  formatImageUrl(imageUrl, width = 150, height = 160) {
    if (!imageUrl) return '';
    
    // If it's already a full URL, use it as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // If it's a Shopify CDN URL, add size parameters
    if (imageUrl.includes('cdn.shopify.com')) {
      const url = new URL(imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl);
      url.searchParams.set('width', width);
      url.searchParams.set('height', height);
      return url.toString();
    }
    
    // For relative URLs, assume it's a Shopify image and add size parameters
    return `${imageUrl}?width=${width}&height=${height}`;
  }
}

// Cart UI Manager - Handles UI updates
class CartUIManager {
  constructor(cartManager) {
    this.cartManager = cartManager;
    this.init();
  }

  init() {
    // Subscribe to cart updates
    this.cartManager.subscribe((cartData) => {
      this.updateCartIcon(cartData);
    });

    // Initialize cart icon update immediately
    this.updateCartIcon(this.cartManager.cartData);
    
    // Also update on DOM ready to catch any server-rendered cart count
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.syncWithServerCartCount();
      });
    } else {
      this.syncWithServerCartCount();
    }
  }

  // Sync with server-rendered cart count on initial load
  syncWithServerCartCount() {
    const existingBadge = document.querySelector('.cart-count-badge');
    if (existingBadge && !this.cartManager.cartData) {
      // If there's a server-rendered badge but no cart data yet, fetch it
      this.cartManager.fetchCartData();
    }
  }

  // Update cart icon in header
  updateCartIcon(cartData) {
    // Only target the specific cart icon in the header, not all cart links
    const cartLink = document.getElementById('cart-icon-link');

    if (cartLink) {
      // Remove existing badge
      const existingBadge = cartLink.querySelector('.cart-count-badge');
      if (existingBadge) {
        existingBadge.remove();
      }

      // Compute visible item count (exclude hidden accessory products)
      const hidden = window.HIDDEN_CART_HANDLES || [];
      const visibleCount = cartData
        ? cartData.items.reduce((sum, item) => {
            return hidden.includes(item.handle) ? sum : sum + item.quantity;
          }, 0)
        : 0;

      // Add new badge if cart has visible items
      if (visibleCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'cart-count-badge absolute -top-1 -right-1 bg-[#B72621] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium';
        badge.textContent = visibleCount;
        cartLink.appendChild(badge);
      }
    }
  }

  // Show success notification
  showSuccessNotification(message = 'Item added to cart!') {
    this.showNotification(message, 'success');
  }

  // Show error notification
  showErrorNotification(message = 'Something went wrong!') {
    this.showNotification(message, 'error');
  }

  // Generic notification system
  showNotification(message, type = 'info') {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.cart-notification');
    existingNotifications.forEach(notification => notification.remove());

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `cart-notification fixed top-4 right-4 z-[60] px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 translate-x-full`;
    
    // Set notification style based on type
    if (type === 'success') {
      notification.classList.add('bg-green-600', 'text-white');
    } else if (type === 'error') {
      notification.classList.add('bg-red-600', 'text-white');
    } else {
      notification.classList.add('bg-blue-600', 'text-white');
    }

    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <span>${message}</span>
        <button class="ml-2 text-white hover:text-gray-200" onclick="this.parentElement.parentElement.remove()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 10);

    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }, 3000);
  }
}

// Initialize cart management system
const cartManager = new CartManager();
const cartUI = new CartUIManager(cartManager);

// Global functions for easy access
window.cartManager = cartManager;
window.cartUI = cartUI;

// Helper function for adding to cart from anywhere
window.addToCartAjax = async function(variantId, quantity = 1, properties = {}) {
  const result = await cartManager.addToCart(variantId, quantity, properties);
  
  if (result.success) {
    cartUI.showSuccessNotification('Item added to cart!');
    
    // Open cart drawer
    if (window.cartDrawer) {
      window.cartDrawer.openDrawer();
    }
  } else {
    cartUI.showErrorNotification(result.error);
  }
  
  return result;
}; 